import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Modal from 'react-modal';
import { FiMessageSquare, FiRefreshCw, FiArrowUpRight, FiInbox, FiChevronLeft, FiChevronRight, FiSearch, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { useAuth } from 'context/auth-context';
import { EmptyState, PageHeader, SkeletonRows } from 'components/ui';
import { listUserQueries, setQueryStatus, UserQuery } from './UserQueries.api';
import './UserQueries.css';
import { TicketStatus } from './TicketDetails';
import { canReply, category, dateLabel, initials } from './UserQueries.helpers';
import { QueryConversation } from './QueryConversation';
import { useLinkedTicket } from './useLinkedTicket';

function Status({ query }: { query: UserQuery }) { return <TicketStatus query={query} />; }
export function UserQueriesPage() {
  const { auth, roleLoading } = useAuth();
  const isAdmin = Number(auth?.role) === 2;
  const [queries, setQueries] = useState<UserQuery[]>([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState<UserQuery | null>(null);
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const clearLinkedTicket = useLinkedTicket(true, isAdmin && !roleLoading, ticket => { setSelected(ticket); setReplyError(''); setNotice(''); }, setNotice);
  useEffect(() => {
    if (!isAdmin || roleLoading) return;
    let active = true;
    setLoading(true);
    setError('');
    listUserQueries(page, search).then(data => {
      if (active) { setQueries(data.queries); setHasMore(data.hasMore); }
    }).catch(err => {
      if (active) setError(err.response?.data?.message || 'Unable to load queries. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAdmin, roleLoading, page, refresh, search]);
  if (roleLoading) return <div className="zs-page"><p role="status" className="uq-gate"><span className="zs-spinner" aria-hidden="true" />Checking admin access…</p></div>;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  const close = () => { if (!sending) { setSelected(null); clearLinkedTicket(); } };
  const open = (query: UserQuery) => { setSelected(query); setReplyError(''); setNotice(''); };
  const changeStatus = async (status: string) => {
    if (!selected || sending) return;
    setSending(true); setReplyError('');
    try {
      const updated = await setQueryStatus(selected, status);
      setSelected(updated);
      setQueries(rows => rows.map(row => row.id === updated.id && row.source === updated.source ? updated : row));
      setNotice('Ticket status updated to ' + status + '.');
    } catch (err: any) { setReplyError(err.response?.data?.message || 'Unable to update status. Please try again.'); }
    finally { setSending(false); }
  };
  const updateConversation = (updated: UserQuery) => {
    setSelected(updated);
    setQueries(rows => rows.map(row => row.id === updated.id && row.source === updated.source ? updated : row));
  };
  return <div className="zs-page">
    <PageHeader
      eyebrow="Member support"
      title="User Queries"
      subtitle="A little support goes a long way. Manage your member conversations here."
      actions={<button className="zs-btn zs-btn-secondary" disabled={loading || sending} onClick={() => setRefresh(value => value + 1)}><FiRefreshCw className={loading ? 'uq-spin' : ''} />Refresh</button>}
    />
    {notice && <p role="status" className="uq-alert uq-alert-info mb-5"><FiInfo aria-hidden="true" /><span>{notice}</span></p>}

    <section className="overflow-hidden zs-card">
      <div className="zs-card-header">
        <div className="flex items-center min-w-0 gap-3">
          <span className="zs-stat-icon"><FiInbox /></span>
          <div className="min-w-0"><h2 className="zs-card-title">Member inbox</h2><p className="zs-card-subtitle">Queries, feedback & support requests</p></div>
        </div>
        <span className="zs-badge shrink-0">Page {page}</span>
      </div>

      <form className="zs-table-toolbar justify-start" role="search" onSubmit={event => { event.preventDefault(); setPage(1); setSearch(searchInput.trim()); setRefresh(value => value + 1); }}>
        <div className="zs-search w-full sm:w-auto sm:flex-1 sm:max-w-md">
          <FiSearch aria-hidden="true" />
          <input className="zs-input h-10" aria-label="Search by name or username" type="search" placeholder="Search by name or username…" maxLength={128} value={searchInput} onChange={event => setSearchInput(event.target.value)} />
        </div>
        <div className="flex gap-2">
          <button type="submit" className="zs-btn zs-btn-primary h-10">Search</button>
          {(searchInput || search) && <button type="button" className="zs-btn zs-btn-ghost h-10" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>Clear</button>}
        </div>
      </form>

      {error ? <div role="alert"><EmptyState icon={<FiAlertTriangle />} title="We couldn’t load your inbox" description={error} action={<button className="zs-btn zs-btn-secondary" onClick={() => setRefresh(value => value + 1)}>Try again</button>} /></div>
        : loading ? <div role="status"><span className="sr-only">Loading conversations…</span><SkeletonRows rows={6} /></div>
        : queries.length === 0 ? <EmptyState icon={<FiMessageSquare />} title="You’re all caught up" description="New member queries will appear here." />
        : <div className="zs-table-wrap is-flush uq-table-scroll"><table className="zs-table">
          <thead><tr>{['Member', 'Query', 'Category', 'Received', 'Status', 'Action'].map(label => <th key={label} scope="col" className={label === 'Action' ? 'text-right' : ''}>{label}</th>)}</tr></thead>
          <tbody>{queries.map(query => <tr key={`${query.source}-${query.id}`}>
            <td><div className="uq-member"><span className="uq-avatar">{initials(query.user_name)}</span><div className="uq-member-text"><strong>{query.user_name || 'Guest member'}</strong><span title={query.email}>{query.email || 'No email provided'}</span></div></div></td>
            <td><button className="uq-query-link" onClick={() => open(query)}><span className="uq-message-preview">{query.message || 'View query'}</span></button></td>
            <td><span className="zs-badge">{category(query)}</span><span className="uq-subtext capitalize">{query.source}</span></td>
            <td><time className="uq-date">{dateLabel(query.created_at)}</time></td>
            <td><Status query={query} /></td>
            <td className="text-right"><button className={`zs-btn zs-btn-sm ${canReply(query) ? 'zs-btn-primary' : 'zs-btn-soft'}`} onClick={() => open(query)}>{canReply(query) ? 'Reply' : 'View conversation'}<FiArrowUpRight /></button></td>
          </tr>)}</tbody>
        </table></div>}

      <footer className="zs-table-footer">
        <span>{loading ? 'Loading…' : error ? 'Inbox unavailable' : `${queries.length} conversation${queries.length === 1 ? '' : 's'} on this page`}</span>
        <div className="flex gap-2">
          <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}><FiChevronLeft />Previous</button>
          <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || !!error || !hasMore} onClick={() => setPage(value => value + 1)}>Next<FiChevronRight /></button>
        </div>
      </footer>
    </section>

    <Modal isOpen={!!selected} onRequestClose={close} shouldCloseOnEsc={!sending} shouldCloseOnOverlayClick={!sending} appElement={document.getElementById('root')} contentLabel="Member conversation" className="zs-modal uq-modal uq-modal-wide" overlayClassName="zs-backdrop uq-overlay">
      {selected && <QueryConversation selected={selected} sending={sending} replyError={replyError} close={close} onUpdate={updateConversation} onBusy={setSending} changeStatus={changeStatus} />}
    </Modal>
  </div>;
}
